/**
 * Cliente HTTP do chat com o Agente Pericial (EW.F3.T07). // ux-neutrality:allow doc-comment
 *
 * Espelha apps/api/types/case_chat.py e apps/api/runtime/case_workspace.py // ux-neutrality:allow doc-comment
 * (rotas /chat estão sob o mesmo prefixo /casos/:id). // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

export type ChatRole = 'user' | 'assistant' | 'system';

export interface ChatMessageData {
  id: string;
  case_id: string;
  role: ChatRole;
  content: string;
  criterion_id: string | null;
  created_at: string;
}

export interface ChatMessageCreateData {
  content: string;
  criterion_id?: string | null;
  contested_signature_id?: string | null;
}

export type ChatErrorReason =
  | 'case_not_found'
  | 'assistant_unavailable'
  | 'assistant_timeout'
  | 'validation'
  | 'no_api_key'
  | 'network'
  | 'unknown';

export class ChatError extends Error {
  public readonly reason: ChatErrorReason;

  constructor(reason: ChatErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'ChatError';
  }
}

const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;
const HTTP_UNPROCESSABLE = 422;
const HTTP_BAD_GATEWAY = 502;
const HTTP_SERVICE_UNAVAILABLE = 503;
const HTTP_GATEWAY_TIMEOUT = 504;

/**
 * Timeout per-request do POST do chat (EM.F1.T01). O backend dá ao Agente um // ux-neutrality:allow doc-comment
 * orçamento de `anthropic_timeout_seconds=50s` (config/settings.py); o cliente // ux-neutrality:allow doc-comment
 * precisa esperar MAIS que isso, senão aborta uma request que o backend ainda // ux-neutrality:allow doc-comment
 * responderia 200. 60s = 50s + margem para overhead de backend/rede. O default // ux-neutrality:allow doc-comment
 * de 15s (api.ts) segue valendo para as demais chamadas (rápidas). // ux-neutrality:allow doc-comment
 */
export const CHAT_REQUEST_TIMEOUT_MS = 60_000;

/**
 * ECONNABORTED/ETIMEDOUT = a request excedeu o timeout do cliente (o Agente // ux-neutrality:allow doc-comment
 * demorou além do nosso orçamento), NÃO uma falha de conectividade. // ux-neutrality:allow doc-comment
 */
function isClientTimeout(err: AxiosError): boolean {
  return err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT';
}

function isNetworkError(err: AxiosError): boolean {
  return err.code === 'ERR_NETWORK';
}

function detailString(err: AxiosError): string {
  const data = err.response?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === 'string' ? data.detail : '';
}

// Mapa direto status HTTP → reason (casos sem ramificação por detail).
const STATUS_REASON: Readonly<Record<number, ChatErrorReason>> = {
  [HTTP_NOT_FOUND]: 'case_not_found',
  [HTTP_GATEWAY_TIMEOUT]: 'assistant_timeout',
  [HTTP_SERVICE_UNAVAILABLE]: 'assistant_unavailable',
  [HTTP_BAD_GATEWAY]: 'assistant_unavailable',
  [HTTP_UNPROCESSABLE]: 'validation',
};

function reasonForHttpStatus(status: number | undefined, detail: string): ChatErrorReason | null {
  if (status === undefined) return null;
  if (status === HTTP_CONFLICT && detail === 'USER_HAS_NO_API_KEY') return 'no_api_key';
  return STATUS_REASON[status] ?? null;
}

export function classifyChatError(err: unknown): ChatError {
  if (!axios.isAxiosError(err)) return new ChatError('unknown');
  const axiosErr = err as AxiosError;

  const byStatus = reasonForHttpStatus(axiosErr.response?.status, detailString(axiosErr));
  if (byStatus !== null) return new ChatError(byStatus);
  // EM.F1.T02: abort por timeout do cliente é "Agente lento", não "Sem conexão".
  if (isClientTimeout(axiosErr)) return new ChatError('assistant_timeout');
  if (isNetworkError(axiosErr)) return new ChatError('network');
  return new ChatError('unknown');
}

export async function postChatMessage(
  client: AxiosInstance,
  caseId: string,
  payload: ChatMessageCreateData,
): Promise<ChatMessageData> {
  try {
    // EM.F1.T01: timeout per-request alinhado ao orçamento do Agente no backend.
    const { data } = await client.post<ChatMessageData>(`/casos/${caseId}/chat`, payload, {
      timeout: CHAT_REQUEST_TIMEOUT_MS,
    });
    return data;
  } catch (err) {
    throw classifyChatError(err);
  }
}

export async function listChatMessages(
  client: AxiosInstance,
  caseId: string,
): Promise<ChatMessageData[]> {
  try {
    const { data } = await client.get<ChatMessageData[]>(`/casos/${caseId}/chat`);
    return data;
  } catch (err) {
    throw classifyChatError(err);
  }
}
